import EditIcon from '@mui/icons-material/Edit';
import {
    Card,
    CardActionArea,
    CardContent,
    Fade,
    Grid,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { selectExperienceDiscounts } from '../../../../app/Slices/DiscountsSlice';
import {
    selectPriceRequired,
    setPriceRequired,
} from '../../../../app/Slices/PriceRequiredSlice';
import {
    useGetDiscountsQuery,
    useGetFeeListQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import {
    camelCaseToSentenceCase,
    camelCaseToTitleCase,
    capitalizeName,
    formatCurrency,
    formatTime,
} from '../../../../shared/utils';
import { CleaningFeeType, FeeApplies } from '../../../enums/experience';
import {
    BaseExperienceCharge,
    ExperienceCharge,
    FlexibleExperienceCharge,
} from '../../../types/experience';
import CleaningFeeModal from '../Owner/Pricing/CleaningFeeModal';
import PricingModalTest from '../Owner/Pricing/PricingModal';
import BasePriceModal from './BasePriceModal';
import FeesModal from './FeesModal';
import { useFormState } from '../Form/useFormState';

export const findFeeById = (id: number, availableFees: any) => {
    return availableFees?.find((fee: any) => fee.id === id);
};

export const formatCleaningFeeDetails = (cleaningFee: any, spaces?: any[]) => {
    if (!cleaningFee) return [];

    const details = [];

    const applies =
        cleaningFee?.applies === FeeApplies.PerNight
            ? 'Applied per night'
            : 'Applied per booking';

    switch (cleaningFee.cleaningFeeType) {
        case CleaningFeeType.Flat:
            details.push(`Flat fee: ${formatCurrency(cleaningFee.price)}`);
            break;

        case CleaningFeeType.PerRoom: {
            const { basePrice, unitPrice } = cleaningFee;

            details.push(`Base fee: ${formatCurrency(basePrice)}`);
            details.push(`Plus ${formatCurrency(unitPrice)} per room`);
            details.push(`${applies}`);
            break;
        }
        case CleaningFeeType.Tiered:
            details.push(`Base: ${formatCurrency(cleaningFee.basePrice)} Plus`);
            if (cleaningFee.tiers && cleaningFee.tiers.length > 0) {
                const tierExamples = cleaningFee.tiers.map(
                    (tier: any) =>
                        `Max ${tier.rooms} rooms: ${formatCurrency(tier.price)}`,
                );
                details.push(...tierExamples);
            }
            details.push(`${applies}`);
            break;

        case CleaningFeeType.ByZone:
            details.push(`Base: ${formatCurrency(cleaningFee.basePrice)}`);

            if (
                (cleaningFee.spaces || spaces) &&
                (cleaningFee.spaces || spaces).length > 0
            ) {
                const zones = (cleaningFee.spaces || spaces)
                    .filter((zone: any) => zone.cleaningFee > 0)
                    .map(
                        (zone: any) =>
                            `${
                                zone.assignedName || zone.name
                            }: ${formatCurrency(zone.cleaningFee)}`,
                    );

                details.push(...zones);
                details.push(`${applies}`);
            }
            break;

        default:
            details.push('Cleaning fee configured');
    }

    return details;
};

interface Props {
    experienceType: string;
    isEditing: boolean;
}

export const getFeeId = (type: string): string => {
    switch (type) {
        case 'perDeskPerDay':
            return 'priceDesk';
        case 'perDeskPerHalfDay':
            return 'priceDeskHalfDay';
        case 'perOfficePerDay':
            return 'priceOffice';
        case 'perOfficePerHalfDay':
            return 'priceOfficeHalfDay';
        case 'perMeetingRoomPerDay':
            return 'priceMeetingRoom';
        case 'perMeetingRoomPerHalfDay':
            return 'priceMeetingRoomHalfDay';
        case 'entireVenuePerDay':
            return 'priceEntireVenue';
        case 'entireVenuePerHalfDay':
            return 'priceEntireVenueHalfDay';
        case 'entireVenuePerNight':
            return 'priceEntireVenue';
        default:
            return '';
    }
};

const getCommercialSpaceConfig = (feeId: string) => {
    switch (feeId) {
        case 'priceDesk':
            return {
                label: 'desk',
                dayType: 'perDeskPerDay',
                halfDayType: 'perDeskPerHalfDay',
            };
        case 'priceOffice':
            return {
                label: 'office',
                dayType: 'perOfficePerDay',
                halfDayType: 'perOfficePerHalfDay',
            };
        case 'priceMeetingRoom':
            return {
                label: 'meeting room',
                dayType: 'perMeetingRoomPerDay',
                halfDayType: 'perMeetingRoomPerHalfDay',
            };
        default:
            return {
                label: 'entire venue',
                dayType: 'entireVenuePerDay',
                halfDayType: 'entireVenuePerHalfDay',
            };
    }
};

const ExperienceChargesForm = ({ experienceType, isEditing }: Props) => {
    const { experience } = useFormState();
    const loggedInUser = UserRole();
    const dispatch = useDispatch();
    const urlRef = window.location.href.split('/')[4];

    const { setValue, getValues, control, trigger } = useFormContext();

    const priceRequired = useSelector(selectPriceRequired);

    const moduleId = parseInt(urlRef);
    const [showModalPrice, setShowModalPrice] = useState(false);
    const [showModalCleaningFee, setShowModalCleaningFee] = useState(false);
    const [showModalFee, setShowModalFee] = useState(false);
    const [spaceDetailsRequired, setSpaceDetailsRequired] = useState(false);

    const [availableSpaces, setAvailableSpaces] = useState<any[]>([]);

    const { data: availableFees = [] } = useGetFeeListQuery(
        loggedInUser?.company,
    );

    const theme = useTheme();

    //get discounts applied to NEW experience
    const newExperienceDiscounts = useSelector(selectExperienceDiscounts);
    const [displayExperienceIdDiscounts, setDisplayExperienceIdDiscounts] =
        useState<any[]>([]);

    //get discounts applied to existing experience
    const { data: getDiscounts } = useGetDiscountsQuery(
        `/owners/${loggedInUser?.company}/discounts`,
    );

    useEffect(() => {
        setDisplayExperienceIdDiscounts(
            newExperienceDiscounts?.length !== 0
                ? newExperienceDiscounts
                : getDiscounts?.filter((i: any) => {
                      return i?.module?.id === moduleId;
                  }),
        );
    }, [getDiscounts, newExperienceDiscounts]);

    const desks = useWatch({ control, name: 'details.desks' });
    const privateOffices = useWatch({
        control,
        name: 'details.privateOffices',
    });
    const meetingRooms = useWatch({
        control,
        name: 'details.meetingRooms',
    });
    const minHireHours = useWatch({ control, name: 'minHireHours' });
    const subType = experience.details.subType;

    useEffect(() => {
        const commercialDetails = {
            desks: desks,
            privateOffices: privateOffices,
            meetingRooms: meetingRooms,
            entireVenue: 1,
        };

        setAvailableSpaces(
            Object.entries(commercialDetails)
                .filter(([_, value]) => parseInt(value) > 0)
                .map(([key, _]) => {
                    switch (key) {
                        case 'desks':
                            return {
                                feeId: 'priceDesk',
                                spaceType: 'desk',
                                unitPrice: '',
                                type: 'perDeskPerDay',
                                chargeType: 'price',
                            };
                        case 'privateOffices':
                            return {
                                feeId: 'priceOffice',
                                spaceType: 'privateOffice',
                                unitPrice: '',
                                type: 'perOfficePerDay',
                                chargeType: 'price',
                            };
                        case 'meetingRooms':
                            return {
                                feeId: 'priceMeetingRoom',
                                spaceType: 'meetingRoom',
                                unitPrice: '',
                                type: 'perMeetingRoomPerDay',
                                chargeType: 'price',
                            };
                        default:
                            return {
                                feeId: 'priceEntireVenue',
                                spaceType: 'entireVenue',
                                unitPrice: '',
                                type: '',
                                chargeType: 'price',
                            };
                    }
                }),
        );

        setSpaceDetailsRequired(experienceType === 'commercial' && !subType);
    }, [desks, privateOffices, meetingRooms, subType, experienceType]);

    const charges: ExperienceCharge[] = useWatch({ control, name: 'charges' });

    const handlePrice = (data: any) => {
        let chargesToSet: ExperienceCharge[] = [];

        if (
            data.chargeType === 'price' ||
            data.chargeType === 'flexiblePrice'
        ) {
            chargesToSet = [data];
        }

        if (Array.isArray(data.prices)) {
            chargesToSet = data.prices;
        }

        if (!chargesToSet.length) {
            console.warn('No valid charges submitted');
            return;
        }

        const currentCharges: ExperienceCharge[] = getValues('charges') || [];

        // Preserve existing non-price charges (fees)
        const preservedCharges = currentCharges.filter(
            (charge) =>
                charge.chargeType !== 'price' &&
                charge.chargeType !== 'flexiblePrice',
        );

        // Merge preserved charges with new price(s)
        const updatedCharges = [...preservedCharges, ...chargesToSet];

        setValue('charges', updatedCharges, {
            shouldValidate: true,
            shouldDirty: true,
        });

        trigger();
        setShowModalPrice(false);
    };

    const handleFees = (data: any) => {
        const charges: ExperienceCharge[] = getValues('charges');

        setValue(
            'charges',
            [
                ...charges.filter((charge) => charge.chargeType !== 'fee'),
                ...data.fees,
            ],
            {
                shouldValidate: true,
                shouldDirty: true,
            },
        );

        trigger();
        setShowModalFee(false);
    };

    const handleCleaningFee = (data: any) => {
        const charges: ExperienceCharge[] = getValues('charges');
        setValue(
            'charges',
            [
                ...charges.filter(
                    (charge) => charge.chargeType !== 'cleaningFee',
                ),
                data,
            ],
            {
                shouldValidate: true,
                shouldDirty: true,
            },
        );
        trigger();
        setShowModalCleaningFee(false);
    };

    const prices: ExperienceCharge[] = charges.filter(
        (charge: any) =>
            charge.chargeType === 'price' ||
            charge.chargeType === 'flexiblePrice',
    );

    const additionalFees = charges.filter(
        (charge: any) => charge.chargeType === 'fee',
    );

    const cleaningFee = charges.find(
        (charge: any) => charge.chargeType === 'cleaningFee',
    );

    const priceWithWeekend = prices.find(
        (price: any) => 'weekendPrice' in price && price?.weekendPrice,
    ) as BaseExperienceCharge | FlexibleExperienceCharge | undefined;
    const weekendDays = priceWithWeekend?.weekendPrice?.days;
    const commercialPrices = prices.filter(
        (price): price is BaseExperienceCharge =>
            price.chargeType === 'price' && 'type' in price,
    );
    const commercialPriceSummaries =
        experienceType === 'commercial'
            ? availableSpaces
                  .map((space) => {
                      const { label, dayType, halfDayType } =
                          getCommercialSpaceConfig(space.feeId);
                      const dayPrice = commercialPrices.find(
                          (price) =>
                              price.type === dayType ||
                              (space.feeId === 'priceEntireVenue' &&
                                  price.type === 'entireVenuePerNight'),
                      );
                      const halfDayPrice = commercialPrices.find(
                          (price) => price.type === halfDayType,
                      );

                      return {
                          key: space.feeId,
                          label,
                          dayPrice,
                          halfDayPrice,
                      };
                  })
                  .filter((summary) => summary.dayPrice || summary.halfDayPrice)
            : [];

    const handleOpenPriceModal = () => {
        if (experienceType === '' || spaceDetailsRequired) {
            return;
        }

        setShowModalPrice(true);
    };

    useEffect(() => {
        if (experienceType !== 'commercial') return;

        const shouldUpdateCharges =
            parseInt(desks) === 0 ||
            parseInt(privateOffices) === 0 ||
            parseInt(meetingRooms) === 0;

        if (!shouldUpdateCharges) return;
        const updatedCharges = [
            ...additionalFees,
            ...prices.filter(
                (price) =>
                    price.chargeType !== 'flexiblePrice' &&
                    'type' in price &&
                    availableSpaces
                        .map((space) => space.feeId)
                        .includes(getFeeId(price.type)),
            ),
        ];
        if (
            updatedCharges.filter((charge) => charge.chargeType === 'price')
                .length > 0
        ) {
            setValue('charges', updatedCharges);
        }
    }, [desks, privateOffices, meetingRooms, subType, experienceType]);

    useEffect(() => {
        if (experienceType === 'commercial') {
            const missingSpaces = availableSpaces.filter(
                (space) =>
                    !prices
                        .map(
                            (price) =>
                                price.chargeType !== 'flexiblePrice' &&
                                'type' in price &&
                                getFeeId(price.type),
                        )
                        .includes(space.feeId),
            );
            dispatch(setPriceRequired(missingSpaces));
        }
    }, [availableSpaces, charges]);

    const handleOpenFeeModal = () => {
        if (experienceType === '') {
            return;
        }

        setShowModalFee(true);
    };
    const handleOpenCleaningFeeModal = () => {
        setShowModalCleaningFee(true);
    };

    return (
        <Grid>
            <Grid container columns={{ xs: 1, sm: 2, md: 3 }} spacing={4}>
                <Grid item xs={1}>
                    <Card
                        onClick={() => {
                            handleOpenPriceModal();
                        }}
                        sx={{
                            boxShadow: 1,
                            background: 'none',
                            height:
                                experienceType === 'commercial'
                                    ? '270px'
                                    : '230px',
                            width: '100%',

                            backgroundColor: 'transparent',
                            color: 'none',
                            cursor: spaceDetailsRequired
                                ? 'default'
                                : 'pointer',
                            '&:hover': {
                                color: theme.palette.neutral.main,
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                            '&:focus': {
                                color: theme.palette.neutral.main,
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                            overflow: 'auto',
                        }}
                        // onClick={() => {
                        //     navigate(`/fees/${fee.id}/edit`);
                        // }}
                        // style={{ flex: 1 }}
                    >
                        <CardActionArea
                            sx={{
                                background: 'none',
                                backgroundColor: 'transparent',
                                color: theme.palette.neutral.main,
                                '&:hover': {
                                    color: theme.palette.neutral.main,
                                    cursor: spaceDetailsRequired
                                        ? 'default'
                                        : 'pointer',
                                    backgroundColor: 'transparent',
                                    background: 'none',
                                },
                                '&:focus': {
                                    color: theme.palette.neutral.main,
                                    backgroundColor: 'transparent',
                                    background: 'none',
                                },
                            }}
                        >
                            <Grid
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: theme.palette.neutral.main,
                                }}
                            >
                                <Grid>
                                    <Typography
                                        gutterBottom
                                        component='div'
                                        sx={{
                                            color: spaceDetailsRequired
                                                ? theme.palette.midGrey.main
                                                : theme.palette.primary.main,
                                            '&:hover': {
                                                color: spaceDetailsRequired
                                                    ? theme.palette.midGrey.main
                                                    : theme.palette.primary
                                                          .main,
                                            },

                                            padding: '0px 5px 0px 0px',
                                            textAlign: 'right',
                                        }}
                                    >
                                        <EditIcon
                                            fontSize='small'
                                            sx={{ fontSize: '16px' }}
                                        />
                                    </Typography>
                                    <CardContent
                                        key={JSON.stringify(prices)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '80px',
                                            paddingTop: 0,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: spaceDetailsRequired
                                                    ? theme.palette.midGrey.main
                                                    : 'primary.main',
                                            }}
                                        >
                                            Base Price
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: 'dark',
                                                fontSize: '12px',
                                                paddingLeft: '2px',
                                            }}
                                        >
                                            {prices?.length &&
                                            'type' in prices[0] &&
                                            prices[0]?.type &&
                                            experienceType !== 'commercial'
                                                ? camelCaseToSentenceCase(
                                                      prices[0]?.type,
                                                  )
                                                : experienceType ===
                                                    'commercial'
                                                  ? ''
                                                  : `Per night/day`}
                                        </Typography>
                                    </CardContent>
                                </Grid>
                            </Grid>
                        </CardActionArea>
                        <CardContent
                            key={JSON.stringify(prices)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '15px',
                            }}
                        >
                            <Grid
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px',
                                }}
                            >
                                {prices?.length > 0 &&
                                    (experienceType === 'commercial'
                                        ? commercialPriceSummaries.map(
                                              ({
                                                  key,
                                                  label,
                                                  dayPrice,
                                                  halfDayPrice,
                                              }) => (
                                                  <Fade
                                                      key={key}
                                                      in={true}
                                                      timeout={500}
                                                  >
                                                      <Typography
                                                          sx={{
                                                              color: 'dark',
                                                              fontSize: '14px',
                                                          }}
                                                      >
                                                          - $
                                                          {Number(
                                                              dayPrice?.unitPrice ??
                                                                  0,
                                                          ).toFixed(2)}{' '}
                                                          {label}/
                                                          {dayPrice?.type ===
                                                          'entireVenuePerNight'
                                                              ? 'night'
                                                              : 'day'}
                                                          {halfDayPrice
                                                              ? ` ($${Number(halfDayPrice.unitPrice).toFixed(2)} half day)`
                                                              : ''}
                                                      </Typography>
                                                  </Fade>
                                              ),
                                          )
                                        : prices?.map(
                                              (price: ExperienceCharge) => (
                                                  <React.Fragment
                                                      key={`${price.chargeType}`}
                                                  >
                                                      <Fade
                                                          in={true}
                                                          timeout={500}
                                                      >
                                                          <Grid
                                                              sx={{
                                                                  display:
                                                                      'flex',
                                                                  gap: '4px',
                                                              }}
                                                          >
                                                              {price.chargeType ===
                                                              'flexiblePrice' ? (
                                                                  <Stack>
                                                                      <Typography
                                                                          sx={{
                                                                              color: 'text.primary',
                                                                              fontSize:
                                                                                  '14px',
                                                                          }}
                                                                      >
                                                                          - $
                                                                          {
                                                                              price.dayPrice
                                                                          }
                                                                          /day
                                                                          {'weekendPrice' in
                                                                              price &&
                                                                              price.weekendPrice &&
                                                                              ` ($${price.weekendPrice.dayPrice} w/e)`}
                                                                          :{' '}
                                                                          {formatTime(
                                                                              price.dayCheckin,
                                                                          )}
                                                                          -
                                                                          {formatTime(
                                                                              price.dayCheckout,
                                                                          )}
                                                                      </Typography>
                                                                      <Typography
                                                                          sx={{
                                                                              color: 'text.primary',
                                                                              fontSize:
                                                                                  '14px',
                                                                          }}
                                                                      >
                                                                          - $
                                                                          {
                                                                              price.nightPrice
                                                                          }
                                                                          /night
                                                                          {'weekendPrice' in
                                                                              price &&
                                                                              price.weekendPrice &&
                                                                              ` ($${price.weekendPrice.nightPrice} w/e)`}
                                                                          :{' '}
                                                                          {formatTime(
                                                                              price.nightCheckin,
                                                                          )}
                                                                          -
                                                                          {formatTime(
                                                                              price.nightCheckout,
                                                                          )}
                                                                      </Typography>
                                                                      <Typography
                                                                          sx={{
                                                                              color: 'text.primary',
                                                                              fontSize:
                                                                                  '14px',
                                                                          }}
                                                                      >
                                                                          - $
                                                                          {
                                                                              price.hourPrice
                                                                          }
                                                                          /hour
                                                                          {'weekendPrice' in
                                                                              price &&
                                                                              price.weekendPrice &&
                                                                              ` ($${price.weekendPrice.hourPrice} w/e)`}
                                                                      </Typography>
                                                                  </Stack>
                                                              ) : (
                                                                  <>
                                                                      <Typography
                                                                          sx={{
                                                                              color: 'dark',
                                                                              fontSize:
                                                                                  '14px',
                                                                          }}
                                                                      >
                                                                          $
                                                                          {Number(
                                                                              'unitPrice' in
                                                                                  price
                                                                                  ? price.unitPrice
                                                                                  : 0,
                                                                          ).toFixed(
                                                                              2,
                                                                          )}{' '}
                                                                          AUD{' '}
                                                                          <span
                                                                              style={{
                                                                                  fontWeight:
                                                                                      'normal',
                                                                              }}
                                                                          >
                                                                              {' '}
                                                                              (ex
                                                                              GST)
                                                                          </span>
                                                                      </Typography>
                                                                      {experienceType ===
                                                                          'commercial' && (
                                                                          <Typography
                                                                              sx={{
                                                                                  color: 'dark',
                                                                                  fontSize:
                                                                                      '14px',
                                                                              }}
                                                                          >
                                                                              {' '}
                                                                              -{' '}
                                                                              {`${camelCaseToSentenceCase(
                                                                                  'type' in
                                                                                      price
                                                                                      ? price?.type
                                                                                      : '',
                                                                              )}`}
                                                                          </Typography>
                                                                      )}
                                                                  </>
                                                              )}
                                                          </Grid>
                                                      </Fade>
                                                      {'weekendPrice' in
                                                          price &&
                                                          price?.weekendPrice &&
                                                          price.chargeType !==
                                                              'flexiblePrice' && (
                                                              <Fade
                                                                  in={
                                                                      'weekendPrice' in
                                                                          price &&
                                                                      !!price?.weekendPrice
                                                                  }
                                                                  timeout={500}
                                                              >
                                                                  <Typography
                                                                      sx={{
                                                                          color: 'dark',
                                                                          fontSize:
                                                                              '14px',
                                                                      }}
                                                                  >
                                                                      $
                                                                      {'weekendPrice' in
                                                                          price &&
                                                                          price
                                                                              ?.weekendPrice
                                                                              ?.unitPrice}{' '}
                                                                      AUD (ex
                                                                      GST) -
                                                                      Weekend
                                                                      Price (
                                                                      {weekendDays?.map(
                                                                          (
                                                                              day: any,
                                                                              index: number,
                                                                          ) => (
                                                                              <span
                                                                                  key={
                                                                                      index
                                                                                  }
                                                                              >
                                                                                  {capitalizeName(
                                                                                      day,
                                                                                  )}
                                                                                  {index <
                                                                                  weekendDays.length -
                                                                                      1
                                                                                      ? ', '
                                                                                      : ''}
                                                                              </span>
                                                                          ),
                                                                      )}
                                                                      )
                                                                  </Typography>
                                                              </Fade>
                                                          )}
                                                  </React.Fragment>
                                              ),
                                          ))}
                                {prices?.length === 0 &&
                                    !(experienceType === '') &&
                                    !spaceDetailsRequired && (
                                        <Fade
                                            in={
                                                prices?.length === 0 &&
                                                !(experienceType === '') &&
                                                !spaceDetailsRequired
                                            }
                                            timeout={500}
                                        >
                                            <Typography
                                                sx={{
                                                    color: 'dark',
                                                    fontSize: '14px',
                                                }}
                                            >
                                                Click to enter the base price
                                                for the experience
                                            </Typography>
                                        </Fade>
                                    )}

                                {experienceType === '' && (
                                    <Fade
                                        in={prices?.length === 0}
                                        timeout={500}
                                    >
                                        <Typography
                                            sx={{
                                                color: 'red',
                                                fontSize: '14px',
                                            }}
                                        >
                                            Please Select Type of Experience
                                        </Typography>
                                    </Fade>
                                )}
                                {spaceDetailsRequired && (
                                    <Fade
                                        in={prices?.length === 0}
                                        timeout={500}
                                    >
                                        <Typography
                                            sx={{
                                                color: 'red',
                                                fontSize: '14px',
                                            }}
                                        >
                                            Please Fill Out the Space Details
                                        </Typography>
                                    </Fade>
                                )}
                                {priceRequired.length > 0 &&
                                    !spaceDetailsRequired &&
                                    experienceType === 'commercial' && (
                                        <Fade
                                            in={priceRequired.length > 0}
                                            timeout={500}
                                        >
                                            <Typography
                                                sx={{
                                                    color: 'red',
                                                    fontSize: '14px',
                                                }}
                                            >
                                                {`No Price Has Been Set for ${
                                                    priceRequired?.length > 1
                                                        ? priceRequired
                                                              .slice(0, -1)
                                                              .map((space) =>
                                                                  camelCaseToTitleCase(
                                                                      space.spaceType,
                                                                  ),
                                                              )
                                                              .join(', ') +
                                                          ' and ' +
                                                          camelCaseToTitleCase(
                                                              priceRequired[
                                                                  priceRequired?.length -
                                                                      1
                                                              ]?.spaceType,
                                                          )
                                                        : priceRequired
                                                              ?.map((space) =>
                                                                  camelCaseToTitleCase(
                                                                      space?.spaceType ||
                                                                          '',
                                                                  ),
                                                              )
                                                              .join('')
                                                }`}
                                            </Typography>
                                        </Fade>
                                    )}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {['property', 'commercial', 'boat'].includes(
                    experienceType,
                ) && (
                    <Grid item xs={1}>
                        <Card
                            onClick={() => {
                                handleOpenCleaningFeeModal();
                            }}
                            sx={{
                                boxShadow: 1,
                                background: 'none',
                                height:
                                    experienceType === 'commercial'
                                        ? '270px'
                                        : '230px',
                                backgroundColor: 'transparent',
                                color: 'none',
                                cursor: 'pointer',
                                '&:hover': {
                                    cursor: 'pointer',
                                    color: theme.palette.neutral.main,
                                    backgroundColor: 'transparent',
                                    background: 'none',
                                },
                                '&:focus': {
                                    cursor: 'pointer',
                                    color: theme.palette.neutral.main,
                                    backgroundColor: 'transparent',
                                    background: 'none',
                                },
                            }}
                            style={{ flex: 1 }}
                        >
                            <CardActionArea
                                sx={{
                                    background: 'none',
                                    backgroundColor: 'transparent',
                                    color: theme.palette.neutral.main,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        color: theme.palette.neutral.main,
                                        backgroundColor: 'transparent',
                                        background: 'none',
                                    },
                                    '&:focus': {
                                        cursor: 'pointer',
                                        color: theme.palette.neutral.main,
                                        backgroundColor: 'transparent',
                                        background: 'none',
                                    },
                                }}
                            >
                                <Grid
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor:
                                            theme.palette.neutral.main,
                                    }}
                                >
                                    <Grid>
                                        <Typography
                                            gutterBottom
                                            component='div'
                                            sx={{
                                                fontSize: '14px',
                                                color: 'dark',
                                                '&:hover': {
                                                    color: 'primary',
                                                },

                                                padding: '0px 5px 0px 0px',
                                                textAlign: 'right',
                                            }}
                                        >
                                            <EditIcon
                                                fontSize='small'
                                                sx={{ fontSize: '16px' }}
                                            />
                                        </Typography>
                                        <CardContent
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                height: '80px',
                                                paddingTop: 0,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: 'primary.main',
                                                }}
                                            >
                                                Cleaning Fee
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    color: 'dark',
                                                    fontSize: '12px',
                                                    paddingLeft: '2px',
                                                }}
                                            >
                                                Flat/Flexible
                                            </Typography>
                                        </CardContent>
                                    </Grid>
                                </Grid>
                            </CardActionArea>
                            <CardContent
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '15px',
                                    overflow: 'auto',
                                    height: '165px',
                                }}
                            >
                                <Grid
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {cleaningFee &&
                                        (() => {
                                            const details =
                                                formatCleaningFeeDetails(
                                                    cleaningFee,
                                                    getValues('spaces'),
                                                );

                                            return details.map(
                                                (detail, detailIndex) => (
                                                    <Fade
                                                        in={true}
                                                        key={detailIndex}
                                                        timeout={500}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                color: 'dark',
                                                                fontSize:
                                                                    '14px',
                                                            }}
                                                        >
                                                            {`${
                                                                details.length >
                                                                1
                                                                    ? '-'
                                                                    : ''
                                                            } ${detail}`}
                                                        </Typography>
                                                    </Fade>
                                                ),
                                            );
                                        })()}
                                    {!cleaningFee && (
                                        <Fade in={!cleaningFee} timeout={500}>
                                            <Typography
                                                sx={{
                                                    color: 'dark',
                                                    fontSize: '14px',
                                                }}
                                            >
                                                Click to enter the cleaning fee
                                                for the experience
                                            </Typography>
                                        </Fade>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                <Grid item xs={1}>
                    <Card
                        onClick={() => {
                            handleOpenFeeModal();
                        }}
                        sx={{
                            boxShadow: 1,
                            background: 'none',
                            // minHeight: isMobile ? '50px' : '150px',
                            // minWidth: '250px',
                            // width: '330px',
                            height:
                                experienceType === 'commercial'
                                    ? '270px'
                                    : '230px',
                            backgroundColor: 'transparent',
                            color: 'none',
                            cursor: 'pointer',
                            '&:hover': {
                                cursor: 'pointer',
                                color: theme.palette.neutral.main,
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                            '&:focus': {
                                cursor: 'pointer',
                                color: theme.palette.neutral.main,
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                        }}
                        // onClick={() => {
                        //     navigate(`/fees/${fee.id}/edit`);
                        // }}
                        style={{ flex: 1 }}
                    >
                        <CardActionArea
                            sx={{
                                background: 'none',
                                backgroundColor: 'transparent',
                                color: theme.palette.neutral.main,
                                '&:hover': {
                                    cursor: 'pointer',
                                    color: theme.palette.neutral.main,
                                    backgroundColor: 'transparent',
                                    background: 'none',
                                },
                                '&:focus': {
                                    cursor: 'pointer',
                                    color: theme.palette.neutral.main,
                                    backgroundColor: 'transparent',
                                    background: 'none',
                                },
                            }}
                        >
                            <Grid
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: theme.palette.neutral.main,
                                }}
                            >
                                <Grid>
                                    <Typography
                                        gutterBottom
                                        component='div'
                                        sx={{
                                            fontSize: '14px',
                                            color: 'dark',
                                            '&:hover': {
                                                color: 'primary',
                                            },

                                            padding: '0px 5px 0px 0px',
                                            textAlign: 'right',
                                        }}
                                    >
                                        <EditIcon
                                            fontSize='small'
                                            sx={{ fontSize: '16px' }}
                                        />
                                    </Typography>
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '80px',
                                            paddingTop: 0,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: 'primary.main',
                                                // fontSize: isIpad ? '16px':''
                                            }}
                                        >
                                            Additional Charges
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: 'dark',
                                                fontSize: '12px',
                                                paddingLeft: '2px',
                                            }}
                                        >
                                            Cleaning, pets, etc
                                        </Typography>
                                    </CardContent>
                                </Grid>
                            </Grid>
                        </CardActionArea>
                        <CardContent
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '15px',
                            }}
                        >
                            <Grid
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {additionalFees?.map(
                                    (fee: any, index: number) => (
                                        <Fade
                                            in={true}
                                            key={index}
                                            timeout={500}
                                        >
                                            <Typography
                                                sx={{
                                                    color: 'dark',
                                                    fontSize: '14px',
                                                }}
                                            >
                                                {`- ${
                                                    typeof fee?.feeId !==
                                                    'number'
                                                        ? ''
                                                        : camelCaseToSentenceCase(
                                                              findFeeById(
                                                                  fee?.feeId,
                                                                  availableFees,
                                                              )?.name || '',
                                                          )
                                                } $${Number(
                                                    fee?.unitPrice,
                                                ).toFixed(2)} AUD (ex GST)`}
                                            </Typography>
                                        </Fade>
                                    ),
                                )}
                                {!(experienceType === '') && (
                                    <Fade
                                        in={additionalFees.length === 0}
                                        timeout={500}
                                    >
                                        <Typography
                                            sx={{
                                                color: 'dark',
                                                fontSize: '14px',
                                            }}
                                        >
                                            No additional charges have been
                                            added
                                        </Typography>
                                    </Fade>
                                )}
                                <Fade in={experienceType === ''} timeout={500}>
                                    <Typography
                                        sx={{
                                            color: 'red',
                                            fontSize: '14px',
                                        }}
                                    >
                                        Please Select Type of Experience
                                    </Typography>
                                </Fade>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <BasePriceModal
                open={experienceType !== 'property' && showModalPrice}
                onClose={() => setShowModalPrice(false)}
                prices={prices}
                experienceType={experienceType}
                availableSpaces={availableSpaces}
                minHireHours={minHireHours}
                onSubmit={handlePrice}
                isEditing={isEditing}
            />
            <PricingModalTest
                open={experienceType === 'property' && showModalPrice}
                onClose={() => setShowModalPrice(false)}
                price={prices}
                experienceType={experienceType}
                availableSpaces={availableSpaces}
                onSubmit={handlePrice}
                isEditing={isEditing}
            />
            <CleaningFeeModal
                open={showModalCleaningFee}
                onClose={() => setShowModalCleaningFee(false)}
                cleaningFee={cleaningFee}
                experienceType={experienceType}
                availableSpaces={availableSpaces}
                onSubmit={handleCleaningFee}
                isEditing={isEditing}
            />

            <FeesModal
                open={showModalFee}
                onClose={() => setShowModalFee(false)}
                onSubmit={handleFees}
                fees={additionalFees}
                experienceType={experienceType}
            />
        </Grid>
    );
};

export default ExperienceChargesForm;
